import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { Card, EmptyState } from "../../components/admin/AdminUI";
import { ACTION_LABEL, BRAND, batchLabel, esc, formatDateTime, handleError, toast } from "./shared";
import ApplyPreviewModal from "./ApplyPreviewModal";

// "1, 2, 3, 5" → "1–3, 5" supaya kolom batch tidak penuh badge.
const compactBatches = (list) => {
  const nums = [...new Set(list.map(Number))].filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
  const parts = [];
  for (let i = 0; i < nums.length; i++) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
    parts.push(j > i + 1 ? `${nums[i]}–${nums[j]}` : j === i + 1 ? `${nums[i]}, ${nums[j]}` : `${nums[i]}`);
    i = j;
  }
  return `${nums.length === 1 ? "Batch" : "Batches"} ${parts.join(", ")}`;
};

// Ikon + warna per jenis perubahan di riwayat konfigurasi.
const HISTORY_ICON = {
  item_created: ["bi-plus-circle", "is-success"],
  item_updated: ["bi-pencil", "is-info"],
  item_activated: ["bi-check-circle", "is-success"],
  item_deactivated: ["bi-slash-circle", "is-danger"],
  batch_item_added: ["bi-box-arrow-in-down", "is-success"],
  batch_item_removed: ["bi-dash-circle", "is-danger"],
};

const Step = ({ n, title, text }) => (
  <div className="spk-step">
    <span className="spk-step-num">{n}</span>
    <div>
      <p className="spk-step-title">{title}</p>
      <p className="spk-step-text">{text}</p>
    </div>
  </div>
);

const SectionTitle = ({ n, children }) => (
  <span className="spk-section-title">
    <span className="spk-step-num is-sm">{n}</span>
    {children}
  </span>
);

// Barang paket (daftar induk) + barang apa saja yang dibagikan di tiap batch.
// Semua daftar barang berasal dari server — tidak ada daftar tetap di sini.
const PackageConfiguration = ({ meta, onChanged }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [configs, setConfigs] = useState(null);
  const [batch, setBatch] = useState(meta.runningBatch);
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [itemFilter, setItemFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [itemRes, batchRes, histRes] = await Promise.all([
        axios.get("/student-package/items"),
        axios.get("/student-package/batches"),
        axios.get("/student-package/config-history"),
      ]);
      setItems(itemRes.data.items);
      setConfigs(batchRes.data.batches);
      setHistory(histRes.data.history);
    } catch (err) {
      handleError(err, navigate, "Unable to load the package configuration.");
      setItems((prev) => prev || []);
      setConfigs((prev) => prev || []);
      setHistory((prev) => prev || []);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, fallback) => {
    setBusy(true);
    try {
      const res = await fn();
      toast(res.data.msg);
      await load();
      onChanged && onChanged();
    } catch (err) {
      handleError(err, navigate, fallback);
    } finally {
      setBusy(false);
    }
  };

  const itemForm = async (item = null) => {
    const result = await Swal.fire({
      title: item ? "Edit package item" : "New package item",
      html: `
        <label for="spk-item-name" style="display:block;text-align:left;font-weight:600;font-size:13px;margin:4px 0">Item name</label>
        <input id="spk-item-name" class="swal2-input" style="margin:0;width:100%" maxlength="100" value="${esc(item ? item.itemName : "")}" placeholder="e.g. Water Bottle">
        <label for="spk-item-desc" style="display:block;text-align:left;font-weight:600;font-size:13px;margin:14px 0 4px">Description (optional)</label>
        <textarea id="spk-item-desc" class="swal2-textarea" style="margin:0;width:100%" maxlength="500" placeholder="Shown to admins only">${esc(item ? item.description || "" : "")}</textarea>`,
      showCancelButton: true,
      confirmButtonText: item ? "Save" : "Create item",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
      focusConfirm: false,
      didOpen: () => document.getElementById("spk-item-name").focus(),
      preConfirm: () => {
        const itemName = document.getElementById("spk-item-name").value.trim();
        const description = document.getElementById("spk-item-desc").value.trim();
        if (itemName.length < 2) {
          Swal.showValidationMessage("Item name must be at least 2 characters.");
          return false;
        }
        return { itemName, description };
      },
    });
    if (!result.isConfirmed) return;
    if (item) run(() => axios.patch(`/student-package/items/${item.id}`, result.value), "Unable to save the item.");
    else run(() => axios.post("/student-package/items", result.value), "Unable to create the item.");
  };

  const toggleActive = async (item) => {
    const activating = !item.isActive;
    const result = await Swal.fire({
      title: activating ? `Activate "${esc(item.itemName)}"?` : `Deactivate "${esc(item.itemName)}"?`,
      text: activating
        ? "The item can be added to batches again."
        : "The item will no longer be given to new students and cannot be added to batches. Existing student records are kept unchanged.",
      icon: activating ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: activating ? "Activate" : "Deactivate",
      cancelButtonText: "Cancel",
      confirmButtonColor: activating ? BRAND : "#dc2626",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    run(() => axios.patch(`/student-package/items/${item.id}`, { isActive: activating }), "Unable to update the item.");
  };

  const batchItems = (configs || []).find((c) => c.batch === batch)?.itemIds || [];

  const toggleBatchItem = async (item, inBatch) => {
    if (inBatch) {
      const result = await Swal.fire({
        title: `Remove "${esc(item.itemName)}" from ${batchLabel(batch)}?`,
        html: "New students in this batch will no longer receive this item.<br/><b>Existing student distribution records are not deleted or changed.</b>",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remove from batch",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;
      run(() => axios.delete(`/student-package/batches/${batch}/items/${item.id}`), "Unable to remove the item.");
    } else {
      run(() => axios.post(`/student-package/batches/${batch}/items`, { packageItemId: item.id }), "Unable to add the item.");
    }
  };

  const copyFrom = async () => {
    const options = Object.fromEntries(
      (configs || []).filter((c) => c.batch !== batch && c.itemIds.length).map((c) => [c.batch, `${batchLabel(c.batch)} — ${c.itemIds.length} items`]),
    );
    if (!Object.keys(options).length) {
      Swal.fire({ text: "No other batch has package items yet.", icon: "info", confirmButtonColor: BRAND });
      return;
    }
    const result = await Swal.fire({
      title: `Copy items into ${batchLabel(batch)}`,
      text: "Items from the chosen batch are added to this batch. Nothing is removed.",
      input: "select",
      inputOptions: options,
      showCancelButton: true,
      confirmButtonText: "Copy items",
      cancelButtonText: "Cancel",
      confirmButtonColor: BRAND,
      reverseButtons: true,
    });
    if (!result.isConfirmed || !result.value) return;
    run(() => axios.post(`/student-package/batches/${batch}/copy`, { fromBatch: result.value }), "Unable to copy the items.");
  };

  if (!items || !configs) return <EmptyState icon="bi-hourglass" title="Loading configuration…" />;

  const activeCount = items.filter((i) => i.isActive).length;
  const inactiveCount = items.length - activeCount;
  const shownItems = items.filter((i) => (itemFilter === "active" ? i.isActive : itemFilter === "inactive" ? !i.isActive : true));
  const batchItemObjs = items.filter((i) => batchItems.includes(i.id));
  const isRunning = batch === meta.runningBatch;

  return (
    <div className="spk-config">
      <div className="spk-steps" aria-label="How package configuration works">
        <Step n="1" title="Create the items" text="List every item the academy can give out." />
        <Step n="2" title="Choose items per batch" text="Tick which items students in each batch receive." />
        <Step n="3" title="Update existing students" text="Optional: give newly added items to students already registered." />
      </div>

      {/* ── Langkah 1: daftar induk barang ── */}
      <Card
        title={<SectionTitle n="1">Package items</SectionTitle>}
        subtitle={`${activeCount} active item${activeCount === 1 ? "" : "s"}${inactiveCount ? ` · ${inactiveCount} inactive` : ""}. Items are never deleted — deactivate an item you no longer give out.`}
        actions={
          <button type="button" className="adm-btn adm-btn--primary" onClick={() => itemForm()} disabled={busy}>
            <i className="bi bi-plus-lg"></i> New item
          </button>
        }
        flush
      >
        {inactiveCount > 0 && (
          <div className="spk-filter" role="tablist" aria-label="Filter items">
            {[
              ["all", "All", items.length],
              ["active", "Active", activeCount],
              ["inactive", "Inactive", inactiveCount],
            ].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={itemFilter === key}
                className={`adm-tab${itemFilter === key ? " adm-tab--active" : ""}`}
                onClick={() => setItemFilter(key)}
              >
                {label} <span className="adm-tab-count">{count}</span>
              </button>
            ))}
          </div>
        )}
        {items.length === 0 ? (
          <EmptyState icon="bi-box-seam" title="No package items yet" text="Click “New item” to create the first one (e.g. T-Shirt, Notebook)." />
        ) : shownItems.length === 0 ? (
          <EmptyState icon="bi-funnel" title="No items in this filter" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table spk-items-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Given to</th>
                  <th>Status</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {shownItems.map((item) => (
                  <tr key={item.id} className={item.isActive ? "" : "spk-row-muted"}>
                    <td>
                      <div className="adm-cell-title">{item.itemName}</div>
                      {item.description && <div className="adm-cell-meta">{item.description}</div>}
                      {item.linkedToOrientation && (
                        <div
                          className="spk-linked"
                          title="When “Has attended Orientation” is ticked in Edit Student, this item is automatically marked as received."
                        >
                          <i className="bi bi-link-45deg" aria-hidden="true"></i> Auto-marked as received when Orientation attendance is ticked
                        </div>
                      )}
                    </td>
                    <td>
                      {item.batches.length ? (
                        <div className="spk-given">
                          <span className="spk-given-range">{compactBatches(item.batches)}</span>
                          {item.batches.includes(meta.runningBatch) ? (
                            <span className="adm-badge adm-badge--success">
                              <i className="bi bi-check2" aria-hidden="true"></i> Current batch
                            </span>
                          ) : (
                            item.isActive && <span className="adm-badge adm-badge--warning">Not in current batch</span>
                          )}
                        </div>
                      ) : (
                        <span className="spk-dim">Not given in any batch yet</span>
                      )}
                    </td>
                    <td>
                      <span className={`adm-status ${item.isActive ? "adm-status--success" : ""}`}>{item.isActive ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="adm-col-actions">
                      <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => itemForm(item)} disabled={busy}>
                        <i className="bi bi-pencil" aria-hidden="true"></i> Edit
                      </button>
                      <button
                        type="button"
                        className={`adm-btn adm-btn--ghost adm-btn--sm ${item.isActive ? "spk-btn-danger" : "spk-btn-success"}`}
                        onClick={() => toggleActive(item)}
                        disabled={busy}
                      >
                        <i className={`bi ${item.isActive ? "bi-slash-circle" : "bi-arrow-counterclockwise"}`} aria-hidden="true"></i>{" "}
                        {item.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Langkah 2 & 3: barang per batch ── */}
      <Card
        title={<SectionTitle n="2">Items for each batch</SectionTitle>}
        subtitle="Pick a batch, then tick the items its students receive. Students who register from now on get these items automatically."
        actions={
          <button type="button" className="adm-btn adm-btn--secondary" onClick={copyFrom} disabled={busy}>
            <i className="bi bi-files"></i> Copy from another batch
          </button>
        }
      >
        <div className="spk-batch-picker" role="tablist" aria-label="Batch to configure">
          {configs.map((c) => (
            <button
              key={c.batch}
              type="button"
              role="tab"
              aria-selected={batch === c.batch}
              className={`adm-tab${batch === c.batch ? " adm-tab--active" : ""}`}
              onClick={() => setBatch(c.batch)}
            >
              Batch {c.batch}
              {c.batch === meta.runningBatch && <span className="spk-current-dot" title="Current batch"></span>}
              <span className="adm-tab-count" title={`${c.itemIds.length} item(s)`}>
                {c.itemIds.length}
              </span>
            </button>
          ))}
        </div>
        <p className="spk-picker-legend">
          <span className="spk-current-dot"></span> current batch · number = items in that batch
        </p>

        <div className="spk-batch-summary">
          <i className="bi bi-box-seam" aria-hidden="true"></i>
          <p>
            New students in <b>{batchLabel(batch)}</b>
            {isRunning && <span className="adm-badge adm-badge--success spk-inline-badge">Current batch</span>} receive{" "}
            <b>
              {batchItems.length} item{batchItems.length === 1 ? "" : "s"}
            </b>
            {batchItemObjs.length > 0 ? <span className="spk-dim">: {batchItemObjs.map((i) => i.itemName).join(", ")}</span> : "."}
          </p>
        </div>

        {items.length === 0 ? (
          <p className="spk-dim">Create package items in step 1 first.</p>
        ) : (
          <ul className="spk-batch-list">
            {items.map((item) => {
              const inBatch = batchItems.includes(item.id);
              // Barang nonaktif yang tidak ada di batch tidak bisa ditambahkan — sembunyikan saja.
              if (!item.isActive && !inBatch) return null;
              return (
                <li key={item.id} className={inBatch ? "is-on" : ""}>
                  <label>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={inBatch}
                      disabled={busy}
                      onChange={() => toggleBatchItem(item, inBatch)}
                    />
                    <span className="spk-batch-name">
                      {item.itemName}
                      {!item.isActive && <span className="spk-tag">inactive</span>}
                    </span>
                    <span className="spk-batch-state">{inBatch ? "Included" : "Not included"}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {batchItems.length === 0 && items.length > 0 && (
          <p className="spk-dim" style={{ margin: "10px 0 0" }}>
            No items in this batch yet. Tick items above, or use “Copy from another batch”.
          </p>
        )}

        <div className="spk-callout is-info spk-apply-callout">
          <span className="spk-step-num is-sm">3</span>
          <div>
            <p className="spk-callout-title">Students already registered in Batch {batch} are not updated automatically</p>
            <p className="spk-callout-text">
              Added a new item? Use this to give it to existing students too. You will see a preview first. It only adds missing items and never removes
              anything.
            </p>
            <button
              type="button"
              className="adm-btn adm-btn--secondary adm-btn--sm"
              onClick={() => setApplyOpen(true)}
              disabled={busy || batchItems.length === 0}
            >
              <i className="bi bi-arrow-repeat"></i> Apply to existing students…
            </button>
          </div>
        </div>
      </Card>

      <Card title="Recent changes" subtitle="Who changed the package configuration, and when.">
        {!history || history.length === 0 ? (
          <p className="adm-muted-text" style={{ margin: 0 }}>
            No changes yet.
          </p>
        ) : (
          <>
            <ul className="spk-history spk-timeline">
              {history.slice(0, historyLimit).map((h) => {
                const [icon, tone] = HISTORY_ICON[h.action] || ["bi-clock-history", ""];
                return (
                  <li key={h.id}>
                    <span className={`spk-timeline-icon ${tone}`}>
                      <i className={`bi ${icon}`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="spk-history-title">
                        {ACTION_LABEL[h.action] || h.action}
                        {h.itemName && (
                          <>
                            {" — "}
                            <b>{h.itemName}</b>
                          </>
                        )}
                        {h.metadata && h.metadata.batch && <span className="adm-badge adm-badge--info spk-inline-badge">Batch {h.metadata.batch}</span>}
                      </p>
                      <p className="spk-history-meta">
                        {formatDateTime(h.performedAt)} · by {h.performedByName || "System"}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
            {history.length > historyLimit && (
              <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setHistoryLimit((n) => n + 30)}>
                Show more ({history.length - historyLimit} older)
              </button>
            )}
          </>
        )}
      </Card>

      {applyOpen && (
        <ApplyPreviewModal
          batch={batch}
          onClose={() => setApplyOpen(false)}
          onApplied={() => {
            load();
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
};

export default PackageConfiguration;
